import { useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase";
import "./LoginPage.css";

type Props = {
  onAuthenticated: () => Promise<void> | void;
};

export default function LoginPage({ onAuthenticated }: Props) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem("inscape_remember_session") === "true",
  );
  const [email, setEmail] = useState(
    () => localStorage.getItem("inscape_remembered_email") ?? "",
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    const form = new FormData(event.currentTarget);
    const loginEmail = String(form.get("email") ?? "")
      .trim()
      .toLowerCase();
    const password = String(form.get("password") ?? "");

    setBusy(true);
    setError("");
    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password,
    });

    if (authError || !data.user) {
      setBusy(false);
      setError(authError?.message ?? "Unable to sign in.");
      return;
    }

    if (rememberMe) {
      localStorage.setItem("inscape_remember_session", "true");
      localStorage.setItem("inscape_remembered_email", loginEmail);
      sessionStorage.removeItem("inscape_current_tab_session");
    } else {
      localStorage.removeItem("inscape_remember_session");
      localStorage.removeItem("inscape_remembered_email");
      sessionStorage.setItem("inscape_current_tab_session", "true");
    }

    try {
      await onAuthenticated();
    } catch (loginError) {
      await supabase.auth.signOut();
      setError(
        loginError instanceof Error
          ? loginError.message
          : "Unable to load your INSCAPE profile.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-visual" aria-label="SeaScape INSCAPE introduction">
        <div className="real-maritime-scene" aria-hidden="true" />
        <div className="real-sea-light" aria-hidden="true" />
        <div className="real-sea-haze" aria-hidden="true" />
        <div className="operations-overlay" aria-hidden="true">
          <span className="operation-status">
            <i /> LIVE ASSET MONITORING
          </span>
          <span className="operation-marker marker-raft">
            <i /> LIFE RAFTS · READY
          </span>
          <span className="operation-marker marker-boat">
            <i /> LIFE BOATS · ONLINE
          </span>
        </div>

        <div className="login-identity">
          <div className="login-brand-lockup">
            <span className="seascape-logo-frame">
              <img
                src="/seascape-logo.png"
                alt="Seascape Synergy logo"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
              <b aria-hidden="true">SS</b>
            </span>
            <strong>INSCAPE</strong>
          </div>
          <p>INSCAPE DEMO SYSTEM</p>
          <h1>Seascape Synergy Assets System</h1>
          <span>
            Monitor life rafts, life boats, service schedules and operational
            readiness from one secure workspace.
          </span>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-panel-motion" aria-hidden="true">
          <i className="panel-orb panel-orb-one" />
          <i className="panel-orb panel-orb-two" />
          <i className="panel-scan" />
          <i className="panel-grid" />
        </div>

        <div className="login-card">
          <div className="mobile-login-brand">
            <span className="seascape-logo-frame">
              <img
                src="/seascape-logo.png"
                alt="Seascape Synergy logo"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
              <b aria-hidden="true">SS</b>
            </span>
            <strong>INSCAPE</strong>
          </div>

          <form onSubmit={submit}>
            <p>SECURE ACCESS</p>
            <h2>Welcome back</h2>
            <span>Sign in to access the INSCAPE asset management dashboard.</span>

            <label>
              Email address
              <input
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@seascapesynergy.com"
                required
                autoComplete="username"
              />
            </label>

            <label>
              Password
              <span className="password-field">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <button
                  className="password-toggle"
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M2.5 12s3.6-6.25 9.5-6.25S21.5 12 21.5 12 17.9 18.25 12 18.25 2.5 12 2.5 12Z" />
                      <circle cx="12" cy="12" r="3.15" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M3 3 21 21" />
                      <path d="M10.6 5.9c.45-.1.92-.15 1.4-.15 5.9 0 9.5 6.25 9.5 6.25a17.2 17.2 0 0 1-2.6 3.25M6.25 6.55A16.8 16.8 0 0 0 2.5 12s3.6 6.25 9.5 6.25c1.2 0 2.3-.26 3.3-.68" />
                      <path d="M9.7 9.7a3.15 3.15 0 0 0 4.6 4.6" />
                    </svg>
                  )}
                </button>
              </span>
            </label>

            <label className="remember-option">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
              />
              <span>Remember me</span>
            </label>

            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}

            <button className="login-button" type="submit" disabled={busy}>
              <span>{busy ? "Signing in…" : "Sign in to dashboard"}</span>
              <b aria-hidden="true">→</b>
            </button>

            <div className="login-security">
              <i>✓</i>
              <span>Secure role-based access</span>
            </div>

            <aside className="demo-login-note" aria-label="Demo login note">
              <div className="demo-note-icon" aria-hidden="true">
                i
              </div>
              <div>
                <strong>Demo login notes</strong>
                <p>
                  <span>Admin</span>
                  <code>wafera@seascapesynergy.com</code>
                </p>
                <p>
                  <span>User</span>
                  <code>sales@seascapesynergy.com</code>
                </p>
                <small>Use the assigned demo password for either account.</small>
              </div>
            </aside>
          </form>
        </div>
      </section>
    </main>
  );
}
